import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Clock, Navigation, Camera, BarChart3, HandCoins, Copy, Check, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useSeo } from "../hooks/useSeo";

// The link a shop owner pastes into WhatsApp to explain what LytePress is. Deliberately no prices,
// no plans and no sign-in wall — that conversation happens later, on /pricing.
const SITE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const SHARE_URL = `${SITE}/api/share/overview`;

const Point = ({ icon: Icon, title, body }) => (
  <div className="flex gap-3.5">
    <span className="shrink-0 h-9 w-9 rounded-xl bg-[#e7f6ec] text-[#0f7a3d] grid place-items-center">
      <Icon size={18} />
    </span>
    <div>
      <p className="font-bold text-[#0f2a1a]">{title}</p>
      <p className="text-sm text-[#44544a] mt-0.5 leading-relaxed">{body}</p>
    </div>
  </div>
);

const Overview = () => {
  const [copied, setCopied] = useState(false);
  useSeo({
    title: "What LytePress is — the deals map for Trinidad & Tobago",
    description:
      "Shops post today's specials, shoppers nearby see them first — closest deals on a map, with directions and how long each one lasts.",
    image: "/og-overview.jpg",
  });

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
      toast.success("Link copied — paste it into any chat");
    } catch {
      toast.error("Couldn't copy. Long-press the link to copy it.");
    }
  };

  return (
    <div className="pb-16" data-testid="overview-page">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl bg-[#0f2a1a] text-white px-6 py-10 sm:px-12 sm:py-14">
        <div
          className="absolute inset-0 opacity-[0.16] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 18% 22%, #4ade80 0, transparent 38%), radial-gradient(circle at 82% 78%, #16a34a 0, transparent 42%)",
          }}
        />
        <div className="relative max-w-2xl">
          <span className="inline-flex items-center gap-2 text-xs font-bold tracking-wide uppercase text-[#a7d8b8] bg-white/10 rounded-full px-3 py-1.5">
            <MapPin size={13} /> Trinidad &amp; Tobago
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
            Today's deals,
            <br />
            <span className="text-[#6ee7a0]">on a map.</span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-white/85 leading-relaxed">
            LytePress is where shops put what's on <em>today</em> — and shoppers nearby see it first.
            Closest deals at the top, directions in one tap, and a countdown so nothing looks fresher
            than it is.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to="/browse"
              data-testid="overview-browse-btn"
              className="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#13913f] text-white font-bold rounded-full px-6 py-3.5 transition-colors"
            >
              See today's deals
            </Link>
            <Link
              to="/register"
              data-testid="overview-register-btn"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/25 text-white font-bold rounded-full px-6 py-3.5 transition-colors"
            >
              Put my shop on the map
            </Link>
          </div>
        </div>
      </section>

      {/* Share row */}
      <section className="mt-6 rounded-2xl border border-[#e3e8e5] bg-white p-5 sm:p-6" data-testid="overview-share">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
          <div className="min-w-0">
            <p className="font-bold text-[#0f2a1a] flex items-center gap-2">
              <Share2 size={16} className="text-[#0f7a3d]" /> Send this to a shop
            </p>
            <p className="text-sm text-[#44544a] mt-1">
              One link that explains LytePress with a proper preview card in WhatsApp.
            </p>
            <p className="text-xs text-[#7b8a81] mt-2 break-all" data-testid="overview-share-url">
              {SHARE_URL.replace(/^https?:\/\//, "")}
            </p>
          </div>
          <div className="flex gap-2 sm:ml-auto shrink-0">
            <button
              onClick={copy}
              data-testid="overview-copy-btn"
              className="inline-flex items-center gap-2 bg-[#0f2a1a] hover:bg-[#16341f] text-white font-bold text-sm rounded-xl px-4 py-3 transition-colors"
            >
              {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Copied" : "Copy link"}
            </button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`What LytePress is — the deals map for Trinidad & Tobago: ${SHARE_URL}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="overview-whatsapp-btn"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1eb85a] text-white font-bold text-sm rounded-xl px-4 py-3 transition-colors"
            >
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Two audiences */}
      <section className="mt-8 grid gap-6 md:grid-cols-2">
        <div className="rounded-2xl border border-[#e3e8e5] bg-white p-6 sm:p-7" data-testid="overview-shoppers">
          <h2 className="text-base md:text-lg font-extrabold text-[#0f2a1a]">If you're shopping</h2>
          <div className="mt-5 space-y-5">
            <Point icon={MapPin} title="Closest first" body="Deals sort by how far they are from you, so you're not scrolling past another island." />
            <Point icon={Clock} title="Nothing stale" body="Every deal carries an end date. When it's over, it comes off the map." />
            <Point icon={Navigation} title="One tap to get there" body="Directions, opening hours and the shop's number sit on every deal." />
          </div>
        </div>

        <div className="rounded-2xl border border-[#e3e8e5] bg-white p-6 sm:p-7" data-testid="overview-shops">
          <h2 className="text-base md:text-lg font-extrabold text-[#0f2a1a]">If you run a shop</h2>
          <div className="mt-5 space-y-5">
            <Point icon={Camera} title="Post in two minutes" body="A photo, the price, the end date. Your pin is on the map the moment you're done." />
            <Point icon={HandCoins} title="No listing fee, no commission" body="Whatever a shopper spends at your counter stays yours. We never touch the sale." />
            <Point icon={BarChart3} title="See what it actually did" body="Views, how many asked for directions, how many grabbed the deal. Plain numbers, no guessing." />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="mt-8 rounded-2xl bg-[#e7f6ec] p-6 sm:p-8" data-testid="overview-how">
        <h2 className="text-base md:text-lg font-extrabold text-[#0f2a1a]">How a deal travels</h2>
        <ol className="mt-5 grid gap-5 sm:grid-cols-3">
          {[
            ["The shop posts it", "Special of the day, end-of-line stock, a slow Tuesday — whatever needs moving."],
            ["Shoppers nearby see it", "It lands on the map and in Browse for people already close to the door."],
            ["They walk in", "Directions, hours, and a code to show at the counter if the shop wants one."],
          ].map(([t, b], i) => (
            <li key={t} className="bg-white rounded-xl p-5">
              <span className="h-7 w-7 rounded-full bg-[#16a34a] text-white font-extrabold text-sm grid place-items-center">
                {i + 1}
              </span>
              <p className="font-bold text-[#0f2a1a] mt-3">{t}</p>
              <p className="text-sm text-[#44544a] mt-1 leading-relaxed">{b}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-[#e3e8e5] bg-white p-6">
        <div>
          <p className="font-extrabold text-[#0f2a1a]">Ready to put your special up?</p>
          <p className="text-sm text-[#44544a] mt-1">Takes two minutes. Questions? support@lytepress.com</p>
        </div>
        <Link
          to="/register"
          data-testid="overview-cta-register"
          className="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#13913f] text-white font-bold rounded-full px-6 py-3.5 transition-colors"
        >
          Register my shop
        </Link>
      </section>
    </div>
  );
};

export default Overview;
